import { CircularProgress, Box, useTheme, alpha, IconButton } from '@mui/material';
import React from 'react';
import { Document, Page } from 'react-pdf';
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

// Update PDFPreview old name .. it should be imagePreview
export const PDFPreview = React.memo(function PDFPreview({
  doc,
  cachedPreviews,
  transformRefs,
  enableZoom = false,
}: {
  doc: { id: string; objectname: string; previewUrl: string };
  cachedPreviews: React.MutableRefObject<Record<string, string>>;
  transformRefs: React.MutableRefObject<Record<string, ReactZoomPanPinchRef>>;
  enableZoom?: boolean;
}) {
  const theme = useTheme();
  const urlWithoutQuery = cachedPreviews.current[doc.id]?.split('?')[0];
  const isImage = urlWithoutQuery?.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: enableZoom ? 500 : 180,
        overflow: 'hidden',
        borderRadius: 1,
        backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff',
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxShadow: `inset 0 0 5px ${alpha(theme.palette.common.black, 0.1)}`,
        flex: 1,
      }}
    >
      <TransformWrapper
        initialScale={enableZoom ? 1 : 0.5}
        minScale={0.1}
        maxScale={5}
        limitToBounds={false}
        centerOnInit
        doubleClick={{ disabled: false }}
        wheel={{
          step: 0.05,
          wheelDisabled: false,
          touchPadDisabled: false,
          activationKeys: [],
        }}
        panning={{ velocityDisabled: false }}
        onInit={instance => {
          transformRefs.current[doc.id] = instance;
        }}
      >
        {({ zoomIn, zoomOut, resetTransform }) => (
          <>
            <TransformComponent
              wrapperStyle={{ width: '100%', height: '100%' }}
              contentStyle={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: 2,
                }}
              >
                {isImage ? (
                  <img
                    src={cachedPreviews.current[doc.id]}
                    alt={doc.objectname}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    }}
                  />
                ) : (
                  <Document
                    file={cachedPreviews.current[doc.id]}
                    loading={<CircularProgress size={24} />}
                    onLoadError={e => console.error('Error loading PDF:', e)}
                  >
                    <Page
                      pageNumber={1}
                      scale={1}
                      renderMode="canvas"
                      width={enableZoom ? 600 : 240}
                      canvasBackground={theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff'}
                    />
                  </Document>
                )}
              </Box>
            </TransformComponent>

            {/* Zoom Controls */}
            {enableZoom && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 10,
                  right: 10,
                  display: 'flex',
                  gap: 1,
                  zIndex: 10,
                  backgroundColor: alpha(theme.palette.background.paper, 0.8),
                  borderRadius: 1,
                  padding: 0.5,
                }}
              >
                <IconButton
                  onClick={() => zoomIn()}
                  size="small"
                  sx={{ backgroundColor: alpha(theme.palette.common.white, 0.8) }}
                >
                  <ZoomInIcon fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={() => zoomOut()}
                  size="small"
                  sx={{ backgroundColor: alpha(theme.palette.common.white, 0.8) }}
                >
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={() => resetTransform()}
                  size="small"
                  sx={{ backgroundColor: alpha(theme.palette.common.white, 0.8) }}
                >
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </>
        )}
      </TransformWrapper>
    </Box>
  );
});
